import { LitElement, html, css, nothing } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { injectInterFont } from "../utils/fonts";

@customElement("cg-composer")
export class CGComposer extends LitElement {
  static override styles = css`
    :host {
      --cg-font: "Inter", sans-serif;
      display: block;
      font-family: "Inter", sans-serif !important;
    }

    *,
    *::before,
    *::after {
      font-family: "Inter", sans-serif !important;
    }

    button,
    textarea {
      font: inherit;
    }

    :host([disabled]) {
      opacity: 0.6;
      pointer-events: none;
    }

    /* ===========================
     Composer Surface
  ============================ */

    .surface {
      position: relative;
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 10px;
      border-radius: 14px;
      background: color-mix(in srgb, var(--cg-text) 4%, var(--cg-bg));
      border: 1px solid var(--cg-border);
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.05);

      transition:
        border-color 160ms ease,
        box-shadow 160ms ease,
        background-color 160ms ease;
    }

    .surface:focus-within {
      border-color: var(--cg-border); /* keep neutral */
      box-shadow: 0 0 0 1px
        color-mix(in srgb, var(--cg-primary) 12%, transparent);
    }

    /* ===========================
     Textarea
  ============================ */

    textarea {
      flex: 1;
      resize: none;
      border: none;
      outline: none;
      background: transparent;
      color: inherit;
      font-size: 14px;
      line-height: 20px;
      min-height: 20px;
      height: 24px;
      max-height: 160px;
      padding: 3px 6px;
      box-sizing: border-box;
      overflow-y: hidden;
    }

    textarea::placeholder {
      color: var(--cg-muted);
    }

    textarea:disabled {
      opacity: 0.5;
    }

    /* ===========================
     Actions
  ============================ */

    .actions {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .icon {
      width: 28px;
      height: 28px;
      border-radius: 999px;
      border: none;
      background: transparent;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
      color: var(--cg-muted);
      font-size: 15px;

      transition:
        background-color 120ms ease,
        transform 120ms ease,
        color 120ms ease;
    }

    .icon:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.06);
      color: var(--cg-text);
    }

    .icon:active:not(:disabled) {
      transform: scale(0.92);
    }

    .icon:disabled {
      opacity: 0.4;
      cursor: default;
    }

    /* ===========================
     Send Icon Accent
  ============================ */

    .send {
      display: flex;
      justify-content: center;
      align-items: center;
      color: var(--cg-primary);
    }

    /* Hover */
    .send:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.06);
      color: var(--cg-primary);
    }

    /* Active */
    .send:active:not(:disabled) {
      transform: scale(0.9);
      background: color-mix(in srgb, var(--cg-primary) 18%, transparent);
    }

    /* Disabled */
    .send:disabled {
      opacity: 0.35;
    }

    /* ===========================
     Emoji Picker
  ============================ */

    .emoji-pop {
      position: absolute;
      right: 8px;
      bottom: calc(100% + 10px);
      z-index: 30;
      border-radius: 14px;
      overflow: hidden;
      background: var(--cg-bg);
      border: 1px solid var(--cg-border);
      box-shadow: var(--cg-shadow);
    }

    .closed-message {
      flex: 1;
      padding: 14px 16px;
      font-family: "Inter", sans-serif !important;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .closed-message-text {
      flex: 1;
      min-width: 0;
    }

    .closed-title {
      font-size: 13px;
      font-weight: 600;
      color: var(--cg-text);
      line-height: 1.3;
    }

    .closed-sub {
      font-size: 11px;
      color: var(--cg-muted);
      margin-top: 2px;
      line-height: 1.4;
    }

    .new-chat-btn {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-shrink: 0;
      padding: 8px 14px;
      border-radius: 10px;
      border: none;
      background: var(--cg-primary);
      color: var(--cg-on-primary);
      font-family: "Inter", sans-serif !important;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      white-space: nowrap;
      transition:
        opacity 150ms ease,
        transform 120ms ease,
        box-shadow 150ms ease;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
    }

    .new-chat-btn:hover {
      opacity: 0.88;
      transform: translateY(-1px);
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.18);
    }

    .new-chat-btn:active {
      transform: scale(0.96);
      box-shadow: none;
    }

    button.new-chat-btn,
    .closed-message,
    .closed-message * {
      font-family: "Inter", sans-serif !important;
      font-synthesis: none;
      letter-spacing: 0;
    }
  `;

  /** Controlled value */
  @property({ type: String }) value = "";

  /** Disabled while sending */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Hide input and show message when conversation is closed */
  @property({ type: Boolean, reflect: true }) closed = false;

  /** Fired when text changes */
  @property({ attribute: false }) onInput?: (v: string) => void;

  /** Fired when user submits */
  @property({ attribute: false }) onSubmit?: () => void;

  /** Fired when user wants to start a new chat from closed state */
  @property({ attribute: false }) onNewChat?: () => void;

  @query("textarea") private textarea?: HTMLTextAreaElement;

  private emojiLoaded = false;
  private showEmoji = false;

  private autoResize(): void {
    const el = this.textarea;
    if (!el) return;

    const MAX_HEIGHT = 160;
    const BASE_HEIGHT = 20; // must match CSS line-height / min-height

    // Reset so scrollHeight recalculates correctly
    el.style.height = "1px";

    const next = el.scrollHeight;

    // Clamp height between base and max
    el.style.height = Math.min(Math.max(next, BASE_HEIGHT), MAX_HEIGHT) + "px";
  }

  private handleInput(e: Event): void {
    const v = (e.target as HTMLTextAreaElement).value;
    this.onInput?.(v);
    this.updateComplete.then(() => this.autoResize());
  }

  private handleKeydown(e: KeyboardEvent): void {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!this.disabled) this.onSubmit?.();
    }
  }

  private async toggleEmoji(): Promise<void> {
    if (!this.emojiLoaded) {
      await import("emoji-picker-element");
      this.emojiLoaded = true;
    }
    this.showEmoji = !this.showEmoji;
    this.requestUpdate();
  }

  private insertEmoji(e: CustomEvent): void {
    const emoji = (e as any).detail?.unicode;
    if (!emoji) return;

    const el = this.textarea;
    if (!el) return;

    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;

    const next = el.value.slice(0, start) + emoji + el.value.slice(end);
    this.onInput?.(next);

    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    });

    this.showEmoji = false;
    this.requestUpdate();
  }

  private handleEscape = (e: KeyboardEvent) => {
    if (e.key === "Escape" && this.showEmoji) {
      this.showEmoji = false;
      this.requestUpdate();
    }
  };

  private handleOutsideClick = (e: MouseEvent) => {
    if (!this.showEmoji) return;

    const path = e.composedPath();

    // If click happened inside this component → ignore
    if (path.includes(this)) return;

    this.showEmoji = false;
    this.requestUpdate();
  };

  override connectedCallback(): void {
    super.connectedCallback();
    injectInterFont(this.shadowRoot ?? this);
    document.addEventListener("click", this.handleOutsideClick);
    document.addEventListener("keydown", this.handleEscape);
  }

  override disconnectedCallback(): void {
    document.removeEventListener("click", this.handleOutsideClick);
    document.removeEventListener("keydown", this.handleEscape);
    super.disconnectedCallback();
  }

  override render() {
    return html`
      <div class="surface">
        ${this.closed
          ? html`
              <div class="closed-message">
                <div class="closed-message-text">
                  <div class="closed-title">Conversation closed</div>
                  <div class="closed-sub">Closed after 24 hours of inactivity</div>
                </div>
                <button class="new-chat-btn" @click=${() => this.onNewChat?.()}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  New chat
                </button>
              </div>
            `
          : html`
              <textarea
                .value=${this.value}
                ?disabled=${this.disabled}
                placeholder="Message..."
                aria-label="Message"
                @input=${(e: Event) => this.handleInput(e)}
                @keydown=${(e: KeyboardEvent) => this.handleKeydown(e)}
              ></textarea>

              <div class="actions">
                <button
                  class="icon"
                  title="Emoji"
                  aria-label="Emoji"
                  @click=${(e: MouseEvent) => {
                    e.stopPropagation();
                    this.toggleEmoji();
                  }}
                >
                  😊
                </button>

                <button
                  class="icon send"
                  title="Send"
                  aria-label="Send"
                  ?disabled=${this.disabled || !this.value.trim()}
                  @click=${() => this.onSubmit?.()}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    aria-hidden="true"
                  >
                    <path
                      d="M7.39999 6.32003L15.89 3.49003C19.7 2.22003 21.77 4.30003 20.51 8.11003L17.68 16.6C15.78 22.31 12.66 22.31 10.76 16.6L9.91999 14.08L7.39999 13.24C1.68999 11.34 1.68999 8.23003 7.39999 6.32003Z"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M10.11 13.6501L13.69 10.0601"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>
            `}
        ${this.showEmoji
          ? html`
              <div
                class="emoji-pop"
                @click=${(e: MouseEvent) => e.stopPropagation()}
              >
                <emoji-picker
                  @emoji-click=${(e: CustomEvent) => this.insertEmoji(e)}
                ></emoji-picker>
              </div>
            `
          : nothing}
      </div>
    `;
  }
}
